/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { useFormikContext } from 'formik';
import { ServiceTierChangeRequestFormValues } from './ServiceTierChangeRequestDialog';
import appTheme from '../../common/theme/appTheme.json';
import CustomPaper from '../../common/CustomPaper';
import CustomListBox from '../../common/CustomListBox';
import { makeStyles } from '@mui/styles/';
import { shallowEqual, useSelector } from 'react-redux';
import { selectAvailableServiceTiers } from '../../../store';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from '../../../utils/StringUtils';

interface ServiceTierChangeRequestDialogFormProps {
  terminalDisplayName: string;
  currentClinAndServiceTier: string | null | undefined;
}

const ServiceTierChangeRequestDialogForm = (props: ServiceTierChangeRequestDialogFormProps) => {
  const { terminalDisplayName, currentClinAndServiceTier } = props;
  const formikContext = useFormikContext<ServiceTierChangeRequestFormValues>();
  const classes = useStyles();
  const availableServiceTiers = useSelector(selectAvailableServiceTiers, shallowEqual);

  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
      <Typography
        sx={{
          width: '100%',
          height: '24px',
          fontFamily: 'Source Sans Pro',
          fontStyle: 'normal',
          fontWeight: 400,
          fontSize: '16px',
          lineHeight: '24px',
        }}
      >
        Complete the fields below to submit your request.
      </Typography>
      <Stack spacing="32px" width="100%" paddingTop="16px">
        <Box width="100%">
          <Typography sx={inputLabelStyle}>Requester name</Typography>
          <TextField
            sx={inputStyle}
            size="small"
            variant="outlined"
            fullWidth
            id="requesterFullName"
            name="requesterFullName"
            value={formikContext?.values?.requesterFullName}
            onChange={formikContext?.handleChange}
            error={formikContext?.touched?.requesterFullName && !!formikContext?.errors?.requesterFullName}
            helperText={formikContext?.touched?.requesterFullName && formikContext?.errors?.requesterFullName}
            InputLabelProps={{ shrink: false }}
          />
        </Box>
        <Box width="100%">
          <Typography sx={inputLabelStyle}>Requester email</Typography>
          <TextField
            sx={inputStyle}
            size="small"
            variant="outlined"
            fullWidth
            id="requesterEmail"
            name="requesterEmail"
            value={formikContext?.values?.requesterEmail}
            onChange={formikContext?.handleChange}
            error={formikContext?.touched?.requesterEmail && !!formikContext?.errors?.requesterEmail}
            helperText={formikContext?.touched?.requesterEmail && formikContext?.errors?.requesterEmail}
            InputLabelProps={{ shrink: false }}
          />
        </Box>
        <Box width="100%">
          <Typography sx={inputLabelStyle}>Terminal</Typography>
          <Typography sx={terminalDataText} data-test="terminal-display-name-form">
            {terminalDisplayName}
          </Typography>
        </Box>
        <Box width="100%">
          <Typography sx={inputLabelStyle}>Current service tier</Typography>
          <Typography sx={terminalDataText} data-test="current-clin-service-tier-form">
            {currentClinAndServiceTier ? currentClinAndServiceTier : EMPTY_FIELD_STRING_PLACEHOLDER }
          </Typography>
        </Box>
        <Box width="100%">
          <Typography sx={inputLabelStyle}>Requested service tier</Typography>
          <Autocomplete
            options={availableServiceTiers}
            classes={{
              root: classes.autocompleteRoot,
              noOptions: classes.autocompleteNoOptions,
              option: classes.autocompleteOptions,
            }}
            id="requestedServiceTier"
            data-test="service-tier-dropdown-form"
            value={formikContext?.values?.requestedServiceTier}
            onChange={(e, value) => formikContext?.setFieldValue('requestedServiceTier', value)}
            PaperComponent={CustomPaper as React.ComponentType<React.HTMLAttributes<HTMLElement>>}
            ListboxComponent={CustomListBox as React.ComponentType<React.HTMLAttributes<HTMLElement>>}
            popupIcon={<KeyboardArrowDown />}
            autoComplete
            renderInput={params => (
              <TextField
                {...params}
                sx={inputStyle}
                id="requestedServiceTier"
                name="requestedServiceTier"
                InputLabelProps={{ shrink: false }}
                placeholder="Select requested service tier"
                fullWidth
                error={formikContext?.touched?.requestedServiceTier && !!formikContext?.errors?.requestedServiceTier}
                helperText={formikContext?.touched?.requestedServiceTier && formikContext?.errors?.requestedServiceTier}
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  className: classes.select,
                }}
                inputProps={{
                  ...params.inputProps,
                  className: classes.input,
                }}
              />
            )}
          />
        </Box>
      </Stack>
    </Box>
  );
};
export default ServiceTierChangeRequestDialogForm;

const inputLabelStyle = {
  fontFamily: 'Source Sans Pro',
  fontStyle: 'normal',
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '20px',
  color: '#9FAFBC',
  paddingBottom: '8px',
};
const inputStyle = {
  input: { color: '#ffffff', fontFamily: 'Source Sans Pro', marginLeft: '2px' },
  textarea: { color: '#ffffff' },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#9FAFBC',
    },
    '&:hover fieldset': {
      borderColor: 'white',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#00C2DE',
    },
  },
};
const terminalDataText = {
  fontFamily: 'Source Sans Pro',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '20px',
  color: '#FFFFFF',
  marginLeft: '16px',
};

export const useStyles = makeStyles({
  select: {
    fontSize: '16px',
    fontFamily: 'Source Sans Pro',
    WebkitBoxSizing: 'border-box',
    MozBoxSizing: 'border-box',
    boxSizing: 'border-box',
    width: '100%',
    height: '40px',
  },
  input: {
    '&::placeholder': {
      color: '#9FAFBC',
      fontFamily: 'Source Sans Pro',
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '20px',
      opacity: 1,
    },
    paddingTop: '8.5px',
    paddingBottom: '8.5px',
    paddingLeft: '7px',
  },
  autocompleteRoot: {
    '& .MuiAutocomplete-popupIndicator': {
      color: appTheme.mptSelectIconGray,
    },
    '& .MuiAutocomplete-clearIndicator': {
      color: appTheme.mptSelectIconGray,
    },
  },
  autocompleteNoOptions: {
    color: appTheme.primaryTextColor,
  },
  autocompleteOptions: {
    '&:hover': {
      backgroundColor: appTheme.primaryBackgroundColor,
    },
    marginBottom: '8px',
    marginTop: '8px',
  },
});
